/** Shared checklist helpers (momentum + puts lanes). */

export function emaLatest(closes, period) {
  if (!closes?.length || closes.length < period) return null;
  const chrono = [...closes].reverse();
  let ema = chrono.slice(0, period).reduce((s, v) => s + v, 0) / period;
  const k = 2 / (period + 1);
  for (let i = period; i < chrono.length; i++) {
    ema = chrono[i] * k + ema * (1 - k);
  }
  return ema;
}

export function calcObvSlope10(closes, volumes) {
  if (!closes?.length || !volumes?.length || closes.length < 16) return null;
  let obv = 0;
  const obvArr = [0];
  for (let i = 0; i < closes.length - 1; i++) {
    if (closes[i] > closes[i + 1]) obv += volumes[i] || 0;
    else if (closes[i] < closes[i + 1]) obv -= volumes[i] || 0;
    obvArr.push(obv);
  }
  const recent = obvArr.slice(0, 5).reduce((s, v) => s + v, 0) / Math.min(5, obvArr.length);
  const prior = obvArr.slice(5, 15).reduce((s, v) => s + v, 0) / Math.min(10, obvArr.length - 5);
  return recent - prior;
}

/** Up-day vs down-day avg volume over last `sessions` completed days. */
export function upDownVolumeAvgs(closes, volumes, sessions = 10) {
  if (!closes?.length || !volumes?.length) return { upAvg: null, downAvg: null };
  let upVol = 0;
  let upN = 0;
  let downVol = 0;
  let downN = 0;
  for (let i = 0; i < sessions && i + 1 < closes.length; i++) {
    const v = volumes[i] || 0;
    if (closes[i] > closes[i + 1]) {
      upVol += v;
      upN += 1;
    } else if (closes[i] < closes[i + 1]) {
      downVol += v;
      downN += 1;
    }
  }
  return {
    upAvg: upN ? upVol / upN : null,
    downAvg: downN ? downVol / downN : null,
  };
}

export function blendedRsVsSpy(result) {
  const pc = result?.phaseContext ?? {};
  const spy20 = pc.spy20dPct ?? result.spyReturn20 ?? null;
  const spy63 = pc.spy63dPct ?? result.spyReturn63 ?? null;
  const stock20 = result.move21d ?? result.stockReturn20 ?? null;
  const stock63 = result.move63d ?? result.stockReturn63 ?? null;
  if (stock20 != null && spy20 != null && stock63 != null && spy63 != null) {
    return (stock20 - spy20 + (stock63 - spy63)) / 2;
  }
  if (result?.rs?.spread != null) return result.rs.spread;
  if (stock63 != null && spy63 != null) return stock63 - spy63;
  if (stock20 != null && spy20 != null) return stock20 - spy20;
  return null;
}

export function hasActiveModule3Top(result) {
  const sig = result?.signal ?? result?.moduleSignal ?? result?.module_signal ?? "";
  if (/TOP_CONFIRMED|TOP_FORMING/i.test(String(sig))) return true;
  const m3 = result?.module3;
  return !!(m3?.setupA?.triggered || m3?.setupB?.triggered || m3?.setupC?.active);
}

export function hasActiveModule3Bottom(result) {
  const sig = result?.signal ?? result?.moduleSignal ?? "";
  if (/BOTTOM_CONFIRMED|BOTTOM_FORMING/i.test(String(sig))) return true;
  const m2 = result?.module2;
  return !!(m2?.setupA?.active || m2?.setupB?.active || m2?.setupC?.active);
}

/** Grade A/B/C from buyScore + advisory gate (display/sort only). */
export function gradeFromScoreAndGate(result, direction = "long") {
  const gate = result?.gate;
  const advisoryClear = !(gate?.failedAdvisory?.length);
  const bs = result?.buyScore ?? 0;
  if (direction === "long") {
    if (bs >= 10 && advisoryClear) return "A";
    if (bs >= 6 && bs <= 9.5) return "B";
    return "C";
  }
  if (bs <= -10 && advisoryClear) return "A";
  if (bs <= -6 && bs >= -9.5) return "B";
  return "C";
}

export function formatNeedsReason(failed) {
  const needs = (failed || [])
    .map((f) => f.needLabel || f.shortLabel)
    .filter(Boolean);
  if (!needs.length) return "";
  return `Needs: ${needs.join(", ")}`;
}

export function checklistFailedIds(failed) {
  return (failed || []).map((f) => f.id).filter(Boolean);
}
